import difference from 'lodash/difference';
import isArray from 'lodash/isArray';
import isEqualWith from 'lodash/isEqualWith';

import { t } from '~/helpers/localization';

import type { InferActionsType } from '~/Reducers';
import type { IClanCommunityUrls } from '~/types/declaration';

export const GET_ACCOUNT_RECOMMENDATIONS_SETTINGS = 'GET_ACCOUNT_RECOMMENDATIONS_SETTINGS';
export const GET_ACCOUNT_RECOMMENDATIONS_SETTINGS_RESPONSE = 'GET_ACCOUNT_RECOMMENDATIONS_SETTINGS_RESPONSE';
export const GET_CLAN_RECOMMENDATIONS_SETTINGS = 'GET_CLAN_RECOMMENDATIONS_SETTINGS';
export const GET_CLAN_RECOMMENDATIONS_SETTINGS_RESPONSE = 'GET_CLAN_RECOMMENDATIONS_SETTINGS_RESPONSE';
export const GET_LEFT_APPLICATION_COUNT = 'GET_LEFT_APPLICATION_COUNT';
export const GET_LEFT_APPLICATION_COUNT_RESPONSE = 'GET_LEFT_APPLICATION_COUNT_RESPONSE';
export const GET_RECOMMENDATIONS = 'GET_RECOMMENDATIONS';
export const GET_RECOMMENDATIONS_RESPONSE = 'GET_RECOMMENDATIONS_RESPONSE';
export const SEND_APPLICATION = 'SEND_APPLICATION';
export const SEND_APPLICATION_RESPONSE = 'SEND_APPLICATION_RESPONSE';
export const SET_ACCOUNT_RECOMMENDATIONS_SETTINGS = 'SET_ACCOUNT_RECOMMENDATIONS_SETTINGS';
export const SET_ACCOUNT_RECOMMENDATIONS_SETTINGS_RESPONSE = 'SET_ACCOUNT_RECOMMENDATIONS_SETTINGS_RESPONSE';
export const SET_CLAN_RECOMMENDATIONS_SETTINGS = 'SET_CLAN_RECOMMENDATIONS_SETTINGS';
export const SET_CLAN_RECOMMENDATIONS_SETTINGS_RESPONSE = 'SET_CLAN_RECOMMENDATIONS_SETTINGS_RESPONSE';

export enum PURPOSES {
  NO_OBLIGATIONS = 'no_obligations',
  SQUAD_BATTLES = 'squad_battles',
  CLAN_BATTLES = 'clan_battles',
}

export enum CLANREC_APPLICABLE_PAGES {
  CATEGORIES = 'categories',
}

export const CLANREC_PAGES = {
  ...PURPOSES,
  ...CLANREC_APPLICABLE_PAGES,
} as const;

export const PURPOSE_TITLES = {
  [PURPOSES.NO_OBLIGATIONS]: t('Никаких обязательств'),
  [PURPOSES.SQUAD_BATTLES]: t('Игра в отряде'),
  [PURPOSES.CLAN_BATTLES]: t('Клановые бои'),
};

export const PurposesMap: Record<PURPOSES, number> = {
  [PURPOSES.NO_OBLIGATIONS]: 0,
  [PURPOSES.SQUAD_BATTLES]: 1,
  [PURPOSES.CLAN_BATTLES]: 2,
};
export const PurposesMapReversed: Record<number, PURPOSES> = {
  0: PURPOSES.NO_OBLIGATIONS,
  1: PURPOSES.SQUAD_BATTLES,
  2: PURPOSES.CLAN_BATTLES,
};

export type AccountRecommendationsSettings = {
  languages: string[];
  useVoiceChat: boolean;
};

export type ClanRecommendationsSettings = {
  languages: string[];
  purpose: PURPOSES;
  isRecommendable: boolean;
  isVoiceChatRequired: boolean;
};

const clanRecommendationsSettingsComparator = (
  a: ClanRecommendationsSettings,
  b: ClanRecommendationsSettings,
): boolean => a === b || (isArray(a) && isArray(b) && !difference(a, b).length);

export const isClanRecommendationsSettingsEqual = (
  a: ClanRecommendationsSettings,
  b: ClanRecommendationsSettings,
): boolean => isEqualWith(a, b, clanRecommendationsSettingsComparator);

export type ClanRecommendation = {
  clanbaseCompletion: number;
  communityUrls: IClanCommunityUrls;
  color: string;
  description: string;
  doesSentAnInvite: boolean;
  id: number;
  isVoiceRequired: boolean;
  languages: Array<string>;
  maxMemberCount: number;
  memberCount: number;
  name: string;
  similarity: number;
  status: string;
  tag: string;
};

export type ActionsType = InferActionsType<typeof actionsRecommendation>;

export const actionsRecommendation = {
  getRecommendations: () =>
    ({
      type: GET_RECOMMENDATIONS,
    }) as const,

  getRecommendationsResponse: (items: ClanRecommendation[]) =>
    ({
      type: GET_RECOMMENDATIONS_RESPONSE,
      payload: items,
    }) as const,

  getLeftApplicationCount: () =>
    ({
      type: GET_LEFT_APPLICATION_COUNT,
    }) as const,

  getAccountRecommendationsSettings: () =>
    ({
      type: GET_ACCOUNT_RECOMMENDATIONS_SETTINGS,
    }) as const,

  getClanRecommendationsSettings: () =>
    ({
      type: GET_CLAN_RECOMMENDATIONS_SETTINGS,
    }) as const,

  getLeftApplicationCountResponse: (leftApplicationCount: number) =>
    ({
      type: GET_LEFT_APPLICATION_COUNT_RESPONSE,
      payload: leftApplicationCount,
    }) as const,

  sendApplication: () =>
    ({
      type: SEND_APPLICATION,
    }) as const,

  sendApplicationResponse: (status: boolean, clan: ClanRecommendation) =>
    ({
      type: SEND_APPLICATION_RESPONSE,
      payload: { status, clan },
    }) as const,

  setClanRecommendationsSettings: () =>
    ({
      type: SET_CLAN_RECOMMENDATIONS_SETTINGS,
    }) as const,

  setClanRecommendationsSettingsResponse: (settings: ClanRecommendationsSettings) =>
    ({
      type: SET_CLAN_RECOMMENDATIONS_SETTINGS_RESPONSE,
      payload: settings,
    }) as const,

  getClanRecommendationsSettingsResponse: (settings: ClanRecommendationsSettings) =>
    ({
      type: GET_CLAN_RECOMMENDATIONS_SETTINGS_RESPONSE,
      payload: settings,
    }) as const,

  getAccountRecommendationsSettingsResponse: (settings: AccountRecommendationsSettings) =>
    ({
      type: GET_ACCOUNT_RECOMMENDATIONS_SETTINGS_RESPONSE,
      payload: settings,
    }) as const,

  setAccountRecommendationsSettings: () =>
    ({
      type: SET_ACCOUNT_RECOMMENDATIONS_SETTINGS,
    }) as const,

  setAccountRecommendationsSettingsResponse: (settings: AccountRecommendationsSettings) =>
    ({
      type: SET_ACCOUNT_RECOMMENDATIONS_SETTINGS_RESPONSE,
      payload: settings,
    }) as const,
};
